import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {  IPortfolioWithVessels, PORTFOLIO, } from './portfolioTypes';
import { omit } from 'lodash';

interface IPortfolioState {
    portfolios: IPortfolioWithVessels[]; // List of portfolios
    selectedPortfolio: IPortfolioWithVessels | null; // Single portfolio (e.g., from getPortfolioById)
    isBusy: boolean; // Flag for transaction state
    isFetched: boolean; // Flag for state is fetched or not - Only for portfolios not for portfolio with vessels
    isPortfoliosLoading: boolean;
    isPortfolioVesselsLoading: boolean;
    error?: string;
}

const initialState: IPortfolioState = {
    portfolios: [],
    selectedPortfolio: null,
    isBusy: false,
    isFetched: false,
    isPortfoliosLoading: false,
    isPortfolioVesselsLoading: false,
    error: undefined,
};

const portfolioSlice = createSlice({
    name: PORTFOLIO,
    initialState,
    reducers: {
        addDraftPortfolioRequest: (state, action: PayloadAction<{ portfolio: IPortfolioWithVessels }>) => {
            state.portfolios = [action.payload.portfolio].concat(state.portfolios);
            state.selectedPortfolio = null;
        },
        deleteDraftPortfolioRequest: (state, action: PayloadAction<{ tempId: string }>) => {
            state.portfolios = state.portfolios.filter((p) => p.tempId !== action.payload.tempId);
            state.selectedPortfolio = null;
        },
        updateDraftPortfolioRequest: (state, action: PayloadAction<{ portfolio: IPortfolioWithVessels }>) => {
            // Not saved draft can be updated or already saved portfolio can be updated            
            if (action.payload.portfolio.id === null && action.payload.portfolio.tempId && action.payload.portfolio.isDraft === true) {
                state.portfolios = state.portfolios.map((p) =>
                    p.tempId === action.payload.portfolio.tempId ? { ...p, ...action.payload.portfolio } : p
                );
            }
            else {
                state.portfolios = state.portfolios.map((p) =>
                    p.id === action.payload.portfolio.id ? { ...p, ...action.payload.portfolio } : p
                );
            }
            state.selectedPortfolio = null;
        },
        addPortfolioRequest: (state, _action: PayloadAction<{ portfolio: IPortfolioWithVessels }>) => {
            state.isBusy = true;
        },
        addPortfolioSuccess: (state, action: PayloadAction<{ portfolio: IPortfolioWithVessels }>) => {
            state.isBusy = false;
            if (action.payload.portfolio.tempId) {
                state.portfolios = state.portfolios.filter((p) => p.tempId !== action.payload.portfolio.tempId);
            }
            state.portfolios.push(omit(action.payload.portfolio, 'tempId'));
            state.selectedPortfolio = omit(action.payload.portfolio, 'tempId');
        },
        updatePortfolioRequest: (state, _action: PayloadAction<{ portfolio: IPortfolioWithVessels }>) => {
            state.isBusy = true;
        },
        updatePortfolioSuccess: (state, action: PayloadAction<{ portfolio: IPortfolioWithVessels }>) => {
            state.isBusy = false;
            state.portfolios = state.portfolios.map((p) =>
                p.id === action.payload.portfolio.id ? action.payload.portfolio : p
            );
            state.selectedPortfolio = action.payload.portfolio;
        },
        deletePortfolioRequest: (state, _action: PayloadAction<{ id: number }>) => {
            state.isBusy = true;
        },
        deletePortfolioSuccess: (state, action: PayloadAction<{ deletedId: number }>) => {
            state.isBusy = false;
            state.portfolios = state.portfolios.filter((p) => p.id !== action.payload.deletedId);
            state.selectedPortfolio = null;
        },
        getPortfoliosRequest: (state) => {
            state.isPortfoliosLoading = true;
            state.isFetched = false;
        },
        getPortfoliosSuccess(state, action: PayloadAction<{ portfolios: IPortfolioWithVessels[] }>) {
            state.isPortfoliosLoading = false;
            state.portfolios = action.payload.portfolios;
            state.isFetched = true;
        },
        getPortfolioWithVesselsRequest: (state, _action: PayloadAction<{ id: number | null, tempId?: string, isDraft?: boolean  }>) => {
            state.isPortfolioVesselsLoading = true;
        },
        getPortfolioWithVesselsSuccess: (state, action: PayloadAction<{ portfolio: IPortfolioWithVessels }>) => {
            state.isPortfolioVesselsLoading = false;
            state.portfolios = state.portfolios.map((p) =>
                p.id === action.payload.portfolio.id ? action.payload.portfolio : p
            );
            state.selectedPortfolio = action.payload.portfolio;
        },
        setSelectedPortfolioRequest: (state, action: PayloadAction<{ id: number | null, tempId?: string, isDraft?: boolean }>) => {
            state.isPortfolioVesselsLoading = false;
            if (action.payload.id === null && action.payload.tempId && action.payload.isDraft === true) {
                state.selectedPortfolio = state.portfolios.find((p) => p.tempId === action.payload.tempId) ?? null;
            }
            else if (action.payload.id !== null) {
                state.selectedPortfolio = state.portfolios.find((p) => p.id === action.payload.id) ?? null;
            }
            else{
                state.selectedPortfolio = null;
            }
        },
        setPortfolioError: (state, action: PayloadAction<{ message: string }>) => {
            state.isPortfoliosLoading = false;
            state.isPortfolioVesselsLoading = false;
            state.isBusy = false;
            state.error = action.payload.message;
        },
        displayOutputByPortfolioIdRequest: (state, _action: PayloadAction<{ id: number | string }>) => {
            state.isBusy = true;
        },
        // displayOutputByPortfolioImoListRequest: (state, _action: PayloadAction<{ imoList: number[], name: string }>) => {
        //     state.isBusy = true;
        // },
        displayOutputByPortfolioSuccess: (state) => {
            state.isBusy = false;
            state.selectedPortfolio = null;
        },
    },
});

export const {
    addDraftPortfolioRequest,
    deleteDraftPortfolioRequest,
    updateDraftPortfolioRequest,
    addPortfolioRequest,
    addPortfolioSuccess,
    updatePortfolioRequest,
    updatePortfolioSuccess,
    deletePortfolioRequest,
    deletePortfolioSuccess,
    getPortfoliosRequest,
    getPortfoliosSuccess,
    getPortfolioWithVesselsRequest,
    getPortfolioWithVesselsSuccess,
    setSelectedPortfolioRequest,
    setPortfolioError,
    displayOutputByPortfolioIdRequest,
    // displayOutputByPortfolioImoListRequest,
    displayOutputByPortfolioSuccess
} = portfolioSlice.actions;

export default portfolioSlice.reducer;

